import { useMemo } from 'react';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import LoadingOverlay from '../../../common/components/skeletons/LoadingOverlay';
import { LabelContent } from '../../../common/components';
import {
  formatAfipDate,
  formatCurrency,
  formatCurrencyRate,
  formatDocument,
  formatVoucherIdentifier,
  getConceptLabel,
} from '../utils/lastInvoiceUtils';

const buildSections = (data) => {
  if (!data || !data.success) return null;

  const {
    sale_point,
    voucher_type,
    voucher_number,
    voucher_number_formatted,
    voucher_date,
    cae,
    cae_expiry_date,
    concept_code,
    customer,
    amounts,
    currency,
    service_period,
  } = data;

  const formattedNumber = voucher_number_formatted || voucher_number;
  const identifier = formatVoucherIdentifier(sale_point, formattedNumber ?? voucher_number);
  const documentLabel = formatDocument(customer?.document_type, customer?.document_number);

  return {
    summary: [
      { label: 'Punto de venta', value: sale_point ?? '-' },
      { label: 'Tipo comprobante', value: voucher_type === 6 ? 'Factura B' : voucher_type ?? '-' },
      { label: 'N° comprobante', value: identifier || formattedNumber || voucher_number || '-' },
      { label: 'CAE', value: cae || '-' },
      { label: 'Vencimiento CAE', value: formatAfipDate(cae_expiry_date) || '-' },
      { label: 'Fecha emisión', value: formatAfipDate(voucher_date) || '-' },
      { label: 'Documento receptor', value: documentLabel || '-' },
      { label: 'Concepto', value: getConceptLabel(concept_code) || '-' },
      { label: 'Moneda', value: currency?.code || '-' },
      { label: 'Cotización', value: formatCurrencyRate(currency?.rate) },
    ],
    amounts: [
      { label: 'Total', value: formatCurrency(amounts?.total) },
      { label: 'Neto', value: formatCurrency(amounts?.net) },
      { label: 'IVA', value: formatCurrency(amounts?.iva) },
      { label: 'Exento', value: formatCurrency(amounts?.exempt) },
      { label: 'Impuestos', value: formatCurrency(amounts?.tax) },
      { label: 'No gravado', value: formatCurrency(amounts?.concept) },
    ],
    service: [
      { label: 'Servicio desde', value: formatAfipDate(service_period?.from) || '-' },
      { label: 'Servicio hasta', value: formatAfipDate(service_period?.to) || '-' },
      { label: 'Vencimiento pago', value: formatAfipDate(service_period?.due) || '-' },
    ],
  };
};

const LastInvoiceDetails = ({ data, errorMessage, isLoading }) => {
  const sections = useMemo(() => buildSections(data), [data]);
  const hasData = Boolean(sections);

  return (
    <Box sx={{ position: 'relative' }}>
      {isLoading && <LoadingOverlay />}
      <Box sx={{ opacity: isLoading ? 0.4 : 1, transition: 'opacity 0.2s ease' }}>
        {hasData ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Datos del comprobante
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={1.5}>
                  {sections.summary.map((row) => (
                    <Grid key={row.label} item xs={12} sm={6}>
                      <LabelContent
                        label={row.label}
                        content={row.value ?? '-'}
                        labelColor="primary.main"
                        sx={{ fontSize: '0.95rem' }}
                        contentSx={{ fontWeight: 600 }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Montos
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={1.5}>
                    {sections.amounts.map((row) => (
                      <Grid key={row.label} item xs={12} sm={6}>
                        <LabelContent
                          label={row.label}
                          content={row.value ?? '-'}
                          labelColor="primary.main"
                          sx={{ fontSize: '0.95rem' }}
                          contentSx={{ fontWeight: 600 }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Período de servicios
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={1.5}>
                    {sections.service.map((row) => (
                      <Grid key={row.label} item xs={12} sm={6}>
                        <LabelContent
                          label={row.label}
                          content={row.value ?? '-'}
                          labelColor="primary.main"
                          sx={{ fontSize: '0.95rem' }}
                          contentSx={{ fontWeight: 600 }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Box
            sx={{
              borderRadius: 2,
              backgroundColor: '#fff',
              boxShadow: 1,
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
              textAlign: 'center',
            }}
          >
            <Typography variant="body1" color="textSecondary">
              {errorMessage ||
                (data && !data.success
                  ? data.error || 'No se pudo obtener la información del comprobante.'
                  : 'Consultá para ver la última factura generada en AFIP.')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LastInvoiceDetails;
